import { useCallback, useMemo, useState } from 'react';
import { MismatchFilters } from '../models';

interface UseMismatchesMainProps {
  initialFilters?: MismatchFilters;
}

// Mapeo de campos de AG-Grid a campos de API
const FIELD_TO_API_SORT: Record<string, string> = {
  order_total: 'order_total',
  courier_total: 'courier_total',
  client_total: 'client_total',
  balance: 'balance',
  last_movement: 'last_movement',
};

export const useMismatchesMain = (props: UseMismatchesMainProps = {}) => {
  const { initialFilters = {} } = props;
  const [filters, setFilters] = useState<MismatchFilters>({
    per_page: 50, // Más registros por página para scroll infinito
    sort_by: 'last_movement',
    sort_direction: 'desc',
    ...initialFilters,
  });

  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>({
    field: 'last_movement',
    direction: 'desc',
  });

  // Estado para búsqueda por referencia
  const [searchTerm, setSearchTerm] = useState('');

  // Convertir sorting de AG-Grid a filtros de API
  const apiFilters = useMemo(() => {
    const apiField = sorting?.field ? FIELD_TO_API_SORT[sorting.field] || sorting.field : undefined;
    const apiDirection = sorting?.direction;

    // Construir filtros base sin sort_by y sort_direction
    const baseFilters = { ...filters };
    delete (baseFilters as any).sort_by;
    delete (baseFilters as any).sort_direction;

    return {
      ...baseFilters,
      ...(searchTerm ? { ref: searchTerm } : {}),
      ...(apiField ? { sort_by: apiField } : {}),
      ...(apiDirection ? { sort_direction: apiDirection } : {}),
    };
  }, [filters, sorting, searchTerm]);

  const updateFilters = useCallback((newFilters: Partial<MismatchFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      per_page: 50,
      sort_by: 'last_movement',
      sort_direction: 'desc',
    });
    setSorting({
      field: 'last_movement',
      direction: 'desc',
    });
    setSearchTerm('');
  }, []);

  const handleSortingChange = useCallback((newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
    setSorting(newSorting);
    // Actualizar filtros con el nuevo sorting
    setFilters((prev) => {
      const rest = { ...prev };
      delete (rest as any).sort_by;
      delete (rest as any).sort_direction;
      return rest;
    });
  }, []);

  // Verificar si hay filtros activos (para mostrar estado vacío correcto)
  const hasActiveFilters = useMemo(() => {
    return !!(
      searchTerm ||
      filters.date_from ||
      filters.date_to ||
      filters.client_id ||
      filters.shop_id ||
      filters.courier_id ||
      filters.tracking_number ||
      filters.mismatch_type
    );
  }, [searchTerm, filters]);

  return {
    filters: apiFilters,
    updateFilters,
    resetFilters,
    handleSortingChange,
    sorting,
    searchTerm,
    handleSearch,
    hasActiveFilters,
  };
};
